import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import InputMask from 'react-input-mask';
import * as s from './styled-customer-form';
import { removeInvalidChars, justNumbers, notifyError } from '../../utils';
import { yupMessages } from '../../constants';

// Lista de UFs — usada no <select> de Estado. Mantida no formato
// label/value pra continuar compativel com o fluxo de submit (envia
// values.estado = 'SP', etc).
const LIST_ESTADO = [
  { label: 'Acre', value: 'AC' },
  { label: 'Alagoas', value: 'AL' },
  { label: 'Amapá', value: 'AP' },
  { label: 'Amazonas', value: 'AM' },
  { label: 'Bahia', value: 'BA' },
  { label: 'Ceará', value: 'CE' },
  { label: 'Distrito Federal', value: 'DF' },
  { label: 'Espírito Santo', value: 'ES' },
  { label: 'Goiás', value: 'GO' },
  { label: 'Maranhão', value: 'MA' },
  { label: 'Mato Grosso', value: 'MT' },
  { label: 'Mato Grosso do Sul', value: 'MS' },
  { label: 'Minas Gerais', value: 'MG' },
  { label: 'Pará', value: 'PA' },
  { label: 'Paraíba', value: 'PB' },
  { label: 'Paraná', value: 'PR' },
  { label: 'Pernambuco', value: 'PE' },
  { label: 'Piauí', value: 'PI' },
  { label: 'Rio de Janeiro', value: 'RJ' },
  { label: 'Rio Grande do Norte', value: 'RN' },
  { label: 'Rio Grande do Sul', value: 'RS' },
  { label: 'Rondônia', value: 'RO' },
  { label: 'Roraima', value: 'RR' },
  { label: 'Santa Catarina', value: 'SC' },
  { label: 'São Paulo', value: 'SP' },
  { label: 'Sergipe', value: 'SE' },
  { label: 'Tocantins', value: 'TO' },
];

// X icon (lucide-style) — usado no botao close do header.
const IX = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

export default ({
  id = 'modal',
  onClose,
  onCloseConfirmation,
  submit,
  title,
  dadosAbaixo,
  sucesso,
  iconFinal,
  telaConcluido,
  setConfirmationModal,
  setTelaConcluido,
  estadoValue,
  msgButton,
  labelButton,
  formikInitialValues,
}) => {
  // Em modo edicao o estadoValue ja vem preenchido; em modo cadastro
  // novo, comeca vazio (operador escolhe no select).
  const [estado, setEstado] = useState(estadoValue || '');

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: yup.object({
      sigla: yup.string().required(yupMessages.REQUIRED),
      name: yup
        .string()
        .required(yupMessages.REQUIRED)
        .typeError(yupMessages.REQUIRED)
        .max(150, yupMessages.MAX(150)),
      cnpj: yup.string().required(yupMessages.REQUIRED),
      email: yup
        .string()
        .email(yupMessages.EMAIL_INVALID)
        .required(yupMessages.REQUIRED),
      endereco: yup.string().required(yupMessages.REQUIRED),
      estado: yup.string().required(yupMessages.REQUIRED),
    }),
  });

  // Submit handler — valida CNPJ + Estado antes de subir pro parent.
  // Em edicao, o parent intercepta values e abre ModalConfirmation
  // (passo 2) antes de bater no backend. Em cadastro novo, o parent
  // dispara o POST direto.
  const onSubmit = (values) => {
    if (justNumbers(values.cnpj).length !== 14) {
      notifyError('CNPJ não está completo');
      return;
    }
    if (!estado) {
      notifyError('Selecione o Estado');
      return;
    }
    setConfirmationModal(true);
    values.estado = estado;
    submit(values);
  };

  // Mantem formik.values.estado em sincronia com o state local
  // (yup valida via formik, mas o source-of-truth visual e o
  // <select>).
  useEffect(() => {
    formik.setFieldValue('estado', estado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  const unfilled =
    !formik.values.sigla ||
    !formik.values.name ||
    !formik.values.cnpj ||
    !formik.values.email ||
    !formik.values.endereco ||
    !estado;

  // Click no scrim (area fora do card) — abre confirmation modal
  // de descarte. Nao fecha direto pra evitar perder dados digitados.
  const handleScrimClick = (e) => {
    if (e.target.id === id && onCloseConfirmation) {
      onCloseConfirmation(true);
    }
  };

  // Tela de sucesso — substitui body+foot quando telaConcluido.
  // Cadastro/edicao concluida: mostra icon + mensagem + botao
  // CONCLUIR que reseta o flag + fecha modal.
  if (telaConcluido) {
    return (
      <s.Scrim id={id} onClick={handleScrimClick}>
        <s.Modal>
          <s.ModalHead>
            <div>
              <s.Title>{title}</s.Title>
            </div>
            <s.Close
              type="button"
              aria-label="Fechar"
              onClick={() => {
                onClose();
                setTelaConcluido(false);
              }}
            >
              {IX}
            </s.Close>
          </s.ModalHead>

          <s.SuccessWrap>
            {iconFinal && <s.SuccessIcon src={iconFinal} alt="" />}
            <s.SuccessText>{sucesso}</s.SuccessText>
          </s.SuccessWrap>

          <s.ModalFoot>
            <s.SaveBtn
              type="button"
              onClick={() => {
                onClose();
                setTelaConcluido(false);
              }}
            >
              Concluir
            </s.SaveBtn>
          </s.ModalFoot>
        </s.Modal>
      </s.Scrim>
    );
  }

  return (
    <s.Scrim id={id} onClick={handleScrimClick}>
      <s.Modal>
        <s.ModalHead>
          <div>
            <s.Title>{title}</s.Title>
            {dadosAbaixo && <s.Desc>{dadosAbaixo}</s.Desc>}
            <s.ReqHint>
              <span className="req">*</span>Itens obrigatórios
            </s.ReqHint>
          </div>
          <s.Close
            type="button"
            aria-label="Fechar"
            onClick={() => onCloseConfirmation && onCloseConfirmation(true)}
          >
            {IX}
          </s.Close>
        </s.ModalHead>

        <s.ModalBody>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(formik.values);
            }}
            noValidate
          >
            <s.FormGrid>
              {/* Sigla — c1 */}
              <s.Field>
                <s.Label htmlFor="sigla">
                  Sigla da Empresa <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="sigla"
                  name="sigla"
                  type="text"
                  placeholder="Ex.: ALCT"
                  maxLength={4}
                  value={formik.values.sigla || ''}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.sigla && !!formik.errors.sigla}
                />
                {formik.touched.sigla && formik.errors.sigla && (
                  <s.ErrorText>{formik.errors.sigla}</s.ErrorText>
                )}
              </s.Field>

              {/* CNPJ — c2 — usa InputMask. Em edicao, fica disabled
                  (CNPJ nao deve mudar apos cadastro). */}
              <s.Field>
                <s.Label htmlFor="cnpj">
                  CNPJ <span className="req">*</span>
                </s.Label>
                <s.MaskWrap
                  $error={formik.touched.cnpj && !!formik.errors.cnpj}
                >
                  <InputMask
                    id="cnpj"
                    name="cnpj"
                    className="inputMasks"
                    mask="99.999.999/9999-99"
                    placeholder="00.000.000/0000-00"
                    value={formik.values.cnpj || ''}
                    onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                    onBlur={formik.handleBlur}
                    disabled={formikInitialValues.cnpj !== ''}
                  />
                </s.MaskWrap>
                {formik.touched.cnpj && formik.errors.cnpj && (
                  <s.ErrorText>{formik.errors.cnpj}</s.ErrorText>
                )}
              </s.Field>

              {/* Razao Social — full */}
              <s.Field $full>
                <s.Label htmlFor="name">
                  Razão Social <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Nome completo da empresa"
                  maxLength={150}
                  value={formik.values.name || ''}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.name && !!formik.errors.name}
                />
                {formik.touched.name && formik.errors.name && (
                  <s.ErrorText>{formik.errors.name}</s.ErrorText>
                )}
              </s.Field>

              {/* Email — full */}
              <s.Field $full>
                <s.Label htmlFor="email">
                  E-Mail <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="contato@empresa.com.br"
                  maxLength={50}
                  value={formik.values.email || ''}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.email && !!formik.errors.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <s.ErrorText>{formik.errors.email}</s.ErrorText>
                )}
              </s.Field>

              {/* Endereco — full */}
              <s.Field $full>
                <s.Label htmlFor="endereco">
                  Endereço <span className="req">*</span>
                </s.Label>
                <s.Input
                  id="endereco"
                  name="endereco"
                  type="text"
                  placeholder="Logradouro, número, cidade, CEP"
                  value={formik.values.endereco || ''}
                  onChange={(e) => formik.handleChange(removeInvalidChars(e))}
                  onBlur={formik.handleBlur}
                  $error={formik.touched.endereco && !!formik.errors.endereco}
                />
                {formik.touched.endereco && formik.errors.endereco && (
                  <s.ErrorText>{formik.errors.endereco}</s.ErrorText>
                )}
              </s.Field>

              {/* Estado — c1, select nativo. */}
              <s.Field>
                <s.Label htmlFor="estado">
                  Estado <span className="req">*</span>
                </s.Label>
                <s.Select
                  id="estado"
                  name="estado"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  onBlur={formik.handleBlur}
                  $error={!estado && formik.submitCount > 0}
                >
                  <option value="">Selecione</option>
                  {LIST_ESTADO.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </s.Select>
              </s.Field>
            </s.FormGrid>
          </form>
        </s.ModalBody>

        <s.ModalFoot>
          {msgButton && <s.Confirm>{msgButton}</s.Confirm>}
          <s.SaveBtn
            type="button"
            onClick={() => onSubmit(formik.values)}
            disabled={unfilled || Object.keys(formik.errors).length > 0}
          >
            {labelButton}
          </s.SaveBtn>
        </s.ModalFoot>
      </s.Modal>
    </s.Scrim>
  );
};
